// components/atoms/AutocompleteInput.tsx
import React, { useState, useEffect } from "react";
import { AutocompleteInputProps, AutocompleteOption } from "./AutocompleteInput.types";
import { Input } from "../Input";

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
    options,
    placeholder = "Buscar...",
    onSelect,
    className,
    label,
    minCharacters = 0,
    value,
}) => {
    const [inputValue, setInputValue] = useState("");
    const [showOptions, setShowOptions] = useState(false);

    // Sync internal state with external value prop
    useEffect(() => {
        if (value !== undefined) {
            const selectedOption = options.find(opt => opt.value === value);
            setInputValue(selectedOption?.label || "");
        }
    }, [value, options]);

    // Only filter if minimum characters are met
    const shouldShowResults = inputValue.length >= minCharacters;

    const filteredOptions = shouldShowResults
        ? options.filter(option =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
        : [];

    const handleSelect = (option: AutocompleteOption) => {
        setInputValue(option.label);
        setShowOptions(false);
        onSelect(option);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowOptions(newValue.length >= minCharacters);
    };

    return (
        <div className={`relative w-full ${className || ''}`}>
            {label && (
                <label className="mb-1 text-sm font-medium">
                    {label}
                </label>
            )}
            <Input
                type="text"
                value={inputValue}
                placeholder={placeholder}
                onChange={handleInputChange}
                onFocus={() => inputValue.length >= minCharacters && setShowOptions(true)}
                onBlur={() => setTimeout(() => setShowOptions(false), 200)}
            />

            {minCharacters > 0 && inputValue.length > 0 && inputValue.length < minCharacters && (
                <p className="text-xs text-muted-foreground mt-1">
                    Escribe al menos {minCharacters} caracteres para buscar
                </p>
            )}

            {showOptions && filteredOptions.length > 0 && (
                <ul className="absolute z-10 bg-white dark:bg-[--secondary-800] border rounded mt-1 w-full max-h-60 overflow-auto">
                    {filteredOptions.map(option => (
                        <li
                            key={option.value}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-[--secondary-700]"
                            onMouseDown={() => handleSelect(option)}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}

            {showOptions && shouldShowResults && filteredOptions.length === 0 && (
                <li className="absolute z-10 bg-white dark:bg-[--secondary-800] border rounded mt-1 w-full px-4 py-6 text-center text-sm text-muted-foreground">
                    Sin datos
                </li>
            )}
        </div>
    );
};
